import { dateRangeFilterValueToRange } from '@app/components/date-range-selector/date-range/date-range-filter-value-to-range';
import { DateRangePreset } from '@app/components/date-range-selector/date-range/date-range-presets';
import { parseAsDateRangeFilter } from '@app/components/date-range-selector/date-range/parse-as-date-range-filter';
import { GenericDateRangeFilter } from '@app/core/list-view/base-filters/GenericDateRangeFilter';
import { GenericTextFilter } from '@app/core/list-view/base-filters/GenericTextFilter';
import { createFilter } from '@app/core/list-view/types';
import { parseAsBoolean, parseAsString } from 'nuqs';
import { defineMessage } from 'react-intl';
import { SavedReportsOnlyMineFilter } from '../components/SavedReportsOnlyMineFilter';

/**
 * Builds the shared date-range filter with a configurable default preset.
 * `toApi` targets nestjsx/crud (Broadcast Evidence). GIP maps via `savedReportsGipFiltersToApi`.
 */
function createDateRangeFilter(defaultPreset: DateRangePreset) {
  return createFilter({
    name: 'dateRange',
    label: false,
    parser: parseAsDateRangeFilter.withDefault({ preset: defaultPreset }),
    isPermanent: true,
    component: GenericDateRangeFilter,
    componentProps: {},
    toApi: (val, context) => {
      const { from, to } = dateRangeFilterValueToRange(val, {
        timezone: context?.timezone as string | undefined,
      });

      const filters: string[] = [];

      if (from && to) {
        filters.push(`createdAt||$between||${from},${to}`);
      } else {
        if (from) {
          filters.push(`createdAt||$gte||${from}`);
        }
        if (to) {
          filters.push(`createdAt||$lte||${to}`);
        }
      }

      return filters;
    },
  });
}

const nameFilter = createFilter({
  name: 'name',
  label: defineMessage({
    id: 'audit.report.name',
    defaultMessage: 'Nombre',
  }),
  parser: parseAsString.withDefault(''),
  isPermanent: true,
  component: GenericTextFilter,
  componentProps: {
    sx: {
      width: '22ch',
    },
  },
  toApi: (val) => {
    const name = typeof val === 'string' ? val.trim() : '';
    return name ? [`name||$contL||${name}`] : null;
  },
});

/**
 * GIP / GIP alt filters: last 7 days, name, and only-mine.
 */
export const SAVED_REPORTS_GIP_FILTERS_CONFIG = [
  createDateRangeFilter('last7Days'),
  nameFilter,
  createFilter({
    name: 'onlyMine',
    label: defineMessage({
      id: 'audit.reportsList.onlyMine',
      defaultMessage: 'Solo míos',
    }),
    parser: parseAsBoolean.withDefault(true),
    isPermanent: true,
    component: SavedReportsOnlyMineFilter,
  }),
];

/**
 * Broadcast Evidence filters: last 30 days and name (no only-mine).
 */
export const SAVED_REPORTS_BROADCAST_EVIDENCE_FILTERS_CONFIG = [
  createDateRangeFilter('last30Days'),
  nameFilter,
];
